import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, Search, Sparkles, Download, ExternalLink, ShieldCheck, Zap, FileText, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

// Same 10-ops catalog used by quotes / rate tables
const OPS = [
  { key: "backup_camera",         label: "Backup Camera" },
  { key: "blind_spot_radar",      label: "Blind Spot Radar" },
  { key: "eyesight",              label: "EyeSight (Subaru)" },
  { key: "front_radar",           label: "Front Radar / ACC" },
  { key: "module_programming",    label: "Module Programming" },
  { key: "night_vision",          label: "Night Vision" },
  { key: "parking_sensors",       label: "Parking Sensors" },
  { key: "surround_view_monitor", label: "Surround-View 360" },
  { key: "wheel_alignment",       label: "Wheel Alignment" },
  { key: "windshield_camera",     label: "Windshield Camera" },
];

const SRC_META = {
  library:     { label: "Verified Library", color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", icon: ShieldCheck },
  public:      { label: "OEM Public Portal", color: "bg-blue-500/10 border-blue-500/30 text-blue-300",         icon: BookOpen },
  ai_research: { label: "AI-assisted (verify)", color: "bg-purple-500/10 border-purple-500/30 text-purple-300", icon: Sparkles },
};

export default function BibliotecaTrueLine() {
  const [searchParams] = useSearchParams();
  const preVin = searchParams.get("vin") || "";
  const preJobId = searchParams.get("job") || "";
  const [vin, setVin] = useState(preVin);
  const [selectedOps, setSelectedOps] = useState(new Set(["windshield_camera", "front_radar"]));
  const [forceAI, setForceAI] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [portals, setPortals] = useState([]);
  const [insurers, setInsurers] = useState([]);

  // Report modal state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportForm, setReportForm] = useState({
    insurance_id: "",
    insurance_name: "",
    customer_name: "",
    claim_number: "",
    technician_name: "Vitor Nascimento",
    technician_credentials: "Certified ADAS Technician · TrueLine Calibration",
  });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/oem-library/portals").then((r) => setPortals(r.data.portals || [])).catch(() => {});
    api.get("/master/insurance").then((r) => setInsurers(r.data || [])).catch(() => {});
  }, []);

  // Auto-run lookup if VIN came in via URL
  useEffect(() => {
    if (preVin && preVin.length >= 11) {
      setTimeout(() => runLookup(), 300);
    }
    // eslint-disable-next-line
  }, []);

  const toggleOp = (k) => {
    setSelectedOps((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const runLookup = async () => {
    const v = vin.trim().toUpperCase();
    if (v.length < 11) return toast.error("VIN inválido (mínimo 11 caracteres)");
    if (selectedOps.size === 0) return toast.error("Selecione ao menos uma operação");
    setLoading(true);
    try {
      const r = await api.post("/oem-library/lookup", {
        vin: v,
        operations: Array.from(selectedOps),
        force_ai: forceAI,
      });
      setLookup(r.data);
      toast.success(`Encontrado ${r.data.results.length} procedimento(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na consulta");
    }
    setLoading(false);
  };

  const openReportModal = () => {
    if (!lookup) return;
    setReportOpen(true);
  };

  const generatePdf = async () => {
    if (!lookup) return;
    if (!reportForm.customer_name.trim()) return toast.error("Nome do cliente obrigatório");
    setGenerating(true);
    try {
      // Resolve insurance name from selection
      const chosenInsurer = insurers.find((i) => i.id === reportForm.insurance_id);
      const resolvedInsuranceName =
        reportForm.insurance_id === "__other__" ? (reportForm.insurance_name || null) :
        chosenInsurer?.name || reportForm.insurance_name || null;
      const payload = {
        insurance_id: reportForm.insurance_id && reportForm.insurance_id !== "__other__" ? reportForm.insurance_id : null,
        insurance_name: resolvedInsuranceName,
        customer_name: reportForm.customer_name || null,
        claim_number: reportForm.claim_number || null,
        technician_name: reportForm.technician_name || "Vitor Nascimento",
        technician_credentials: reportForm.technician_credentials || null,
        job_id: preJobId || null,
      };
      const r = await api.post(`/oem-library/lookup/${lookup.id}/pdf`, payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const fname = reportForm.claim_number
        ? `oem-report-claim-${reportForm.claim_number}-${lookup.vin}.pdf`
        : `oem-report-${lookup.vin}-${lookup.id.slice(0,8)}.pdf`;
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success("Relatório profissional baixado");
      setReportOpen(false);
    } catch (e) { toast.error("Falha ao gerar PDF"); }
    setGenerating(false);
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="biblioteca-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-cyan-600/15 border border-cyan-500/20 flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">TrueLine</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Biblioteca TrueLine</h1>
          <p className="text-sm text-slate-400 mt-1">Consulta OEM por VIN — cita fontes públicas (I-CAR, portais das montadoras, NHTSA) + IA para procedimentos raros.</p>
        </div>
      </div>

      {/* Input card */}
      <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.02] p-5">
        <div className="grid md:grid-cols-[1fr_auto] gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 flex items-center gap-1.5"><Search className="w-3 h-3" />VIN</label>
            <Input data-testid="vin-input" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} placeholder="17-character VIN" className="bg-[#030712] border-slate-800 font-mono uppercase" maxLength={17} />
          </div>
          <div className="flex items-end">
            <Button data-testid="run-lookup-btn" onClick={runLookup} disabled={loading} className="bg-cyan-600 hover:bg-cyan-500 h-10 min-w-[180px]">
              {loading ? "Buscando..." : <><Search className="w-4 h-4 mr-1.5" />Buscar Procedimentos</>}
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Operações ADAS</div>
          <div className="flex flex-wrap gap-1.5">
            {OPS.map((op) => {
              const sel = selectedOps.has(op.key);
              return (
                <button
                  key={op.key}
                  data-testid={`op-${op.key}`}
                  onClick={() => toggleOp(op.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${sel ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200" : "bg-slate-900/40 border-slate-800 text-slate-400 hover:text-slate-200"}`}
                >{op.label}</button>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <input data-testid="force-ai" type="checkbox" checked={forceAI} onChange={(e) => setForceAI(e.target.checked)} className="w-3.5 h-3.5" />
          <label className="text-[11px] text-slate-400 flex items-center gap-1"><Sparkles className="w-3 h-3 text-purple-400" />Forçar pesquisa IA (mais lento, ~$0.005/op)</label>
        </div>
      </div>

      {/* Results */}
      {lookup && (
        <div className="space-y-4" data-testid="results-panel">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="text-slate-400">Veículo: </span>
              <span className="text-slate-100 font-medium">{lookup.year} {lookup.make} {lookup.model}</span>
              <span className="text-slate-600 ml-3 font-mono text-xs">VIN {lookup.vin}</span>
            </div>
            <Button data-testid="download-pdf-btn" onClick={openReportModal} className="bg-emerald-600 hover:bg-emerald-500">
              <Download className="w-4 h-4 mr-1.5" />Gerar Relatório PDF
            </Button>
          </div>

          <div className="space-y-3">
            {lookup.results.map((r, i) => {
              const meta = SRC_META[r.source] || SRC_META.public;
              const SrcIcon = meta.icon;
              return (
                <div key={i} data-testid={`result-${r.operation}`} className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-display text-lg font-medium">{r.operation_label}</h3>
                      <div className={`inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${meta.color}`}>
                        <SrcIcon className="w-3 h-3" />{meta.label}
                      </div>
                    </div>
                  </div>

                  {r.summary && <p className="text-sm text-slate-300 leading-relaxed">{r.summary}</p>}

                  {(r.tools || []).length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 flex items-center gap-1"><Zap className="w-3 h-3" />Tools</div>
                      <div className="flex flex-wrap gap-1">
                        {r.tools.map((t, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-300">{t}</span>)}
                      </div>
                    </div>
                  )}

                  {r.risk_notes && (
                    <div className="mt-3 rounded-md border border-rose-500/20 bg-rose-500/5 p-3">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-rose-400 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Risk if skipped</div>
                      <p className="text-xs text-rose-100/70 leading-relaxed">{r.risk_notes}</p>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-slate-800 flex flex-wrap gap-2">
                    {r.oem_source_url && (
                      <a data-testid={`link-oem-${r.operation}`} href={r.oem_source_url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20">
                        <ExternalLink className="w-3 h-3" />{r.oem_source_name || "OEM Portal"}
                      </a>
                    )}
                    {r.icar_url && (
                      <a href={r.icar_url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20">
                        <ExternalLink className="w-3 h-3" />I-CAR RTS
                      </a>
                    )}
                    {(r.other_urls || []).map((u, j) => (
                      <a key={j} href={u} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300 hover:text-slate-100 max-w-[280px] truncate">
                        <ExternalLink className="w-3 h-3" />{u.replace(/^https?:\/\//, "").slice(0, 40)}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* OEM portals catalog (footer reference) */}
      {portals.length > 0 && (
        <details className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
          <summary className="text-xs uppercase tracking-wider font-bold text-slate-500 cursor-pointer hover:text-slate-300 flex items-center gap-1.5">
            <FileText className="w-3 h-3" />Fontes públicas configuradas ({portals.length} montadoras)
          </summary>
          <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-3 gap-1">
            {portals.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer" className="text-xs px-2 py-1.5 rounded hover:bg-slate-900/40 text-slate-400 hover:text-slate-100 flex items-center gap-1.5 truncate">
                <ExternalLink className="w-3 h-3 shrink-0" />
                <span className="font-medium">{p.make}</span> <span className="text-slate-600">·</span> <span className="truncate">{p.name}</span>
              </a>
            ))}
          </div>
        </details>
      )}

      {/* Report modal */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-400" /> Gerar Relatório Profissional
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2" data-testid="report-modal">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Seguradora</Label>
              <select
                data-testid="report-insurance"
                value={reportForm.insurance_id}
                onChange={(e) => setReportForm({ ...reportForm, insurance_id: e.target.value, insurance_name: "" })}
                className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-md h-9 px-3 text-sm text-slate-200"
              >
                <option value="">-- selecione --</option>
                {insurers.map((ins) => <option key={ins.id} value={ins.id}>{ins.name}{ins.short_code ? ` (${ins.short_code})` : ""}</option>)}
                <option value="__other__">Outra (digitar nome)</option>
              </select>
              {reportForm.insurance_id === "__other__" && (
                <Input
                  data-testid="report-insurance-name"
                  value={reportForm.insurance_name}
                  onChange={(e) => setReportForm({ ...reportForm, insurance_name: e.target.value })}
                  placeholder="Nome da seguradora"
                  className="mt-2 bg-[#030712] border-slate-800"
                />
              )}
              <div className="text-[10px] text-slate-500 mt-1.5">
                {insurers.length} seguradora(s) cadastrada(s).
                <a href="/master/insurance" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 ml-1 underline underline-offset-2">Gerenciar em Master Data →</a>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Nome do Cliente *</Label>
              <Input data-testid="report-customer" value={reportForm.customer_name} onChange={(e) => setReportForm({ ...reportForm, customer_name: e.target.value })} placeholder="Ex: John Doe" className="mt-1 bg-[#030712] border-slate-800" autoFocus />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Número do Claim</Label>
              <Input data-testid="report-claim" value={reportForm.claim_number} onChange={(e) => setReportForm({ ...reportForm, claim_number: e.target.value })} placeholder="Ex: CLM-2026-00847" className="mt-1 bg-[#030712] border-slate-800 font-mono" />
            </div>
            <div className="border-t border-slate-800 pt-3">
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Assinado por (Técnico)</Label>
              <Input data-testid="report-tech-name" value={reportForm.technician_name} onChange={(e) => setReportForm({ ...reportForm, technician_name: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" />
              <Input data-testid="report-tech-creds" value={reportForm.technician_credentials} onChange={(e) => setReportForm({ ...reportForm, technician_credentials: e.target.value })} className="mt-2 bg-[#030712] border-slate-800 text-xs" placeholder="Certificação / cargo" />
            </div>
            <Button data-testid="report-generate-btn" onClick={generatePdf} disabled={generating} className="bg-emerald-600 hover:bg-emerald-500 w-full">
              <Download className="w-4 h-4 mr-2" />{generating ? "Gerando..." : "Baixar Relatório PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
